import type { MacroPackageRunReply } from '@playcastdotio/Messaging';

interface OwnedRun { packageSha256: string; startRequestId?: string; earlyTerminal?: MacroPackageRunReply;
  terminal?: MacroPackageRunReply }
const isTerminal = (reply: MacroPackageRunReply) => ['completed', 'cancelled', 'failed'].includes(reply.outcome)
  && reply.errorCode !== 'run_not_found';

/** Retains exact terminal acknowledgements after Native removes a run, within one control-session owner. */
export class LocalPackageRunHistory {
  private readonly runs = new Map<string, OwnedRun>();
  private readonly terminalOrder: string[] = [];
  public constructor(private readonly terminalLimit = 64) {}
  public claim(runId: string, packageSha256: string): void { this.runs.set(runId, { packageSha256 }); }
  public owns(runId: string): boolean { return this.runs.has(runId); }
  public terminal(runId: string): MacroPackageRunReply | undefined { return this.runs.get(runId)?.terminal; }
  public active(): string[] { return [...this.runs].filter(([, run]) => !run.terminal
    || run.terminal.errorCode === 'input_release_unconfirmed').map(([id]) => id); }
  public forget(runId: string): void { this.runs.delete(runId); }
  public clear(): void { this.runs.clear(); this.terminalOrder.length = 0; }
  public admitted(runId: string, reply: MacroPackageRunReply): void {
    const run = this.runs.get(runId);
    if (!run || reply.runId !== runId || !this.samePackage(run, reply)) throw new Error('local_package_native_identity_invalid');
    run.startRequestId = reply.requestId;
    if (run.earlyTerminal?.requestId === reply.requestId) this.retain(run, run.earlyTerminal);
    delete run.earlyTerminal;
    if (isTerminal(reply)) this.retain(run, reply);
  }
  public observe(reply: MacroPackageRunReply): void {
    const run = this.runs.get(reply.runId);
    if (!run || !isTerminal(reply) || !this.samePackage(run, reply) || run.terminal) return;
    if (!run.startRequestId) { run.earlyTerminal = reply; return; }
    if (reply.requestId === run.startRequestId) this.retain(run, reply);
  }
  /** A correlated status/cancel reply has its own request ID; a failed reply never proves release. */
  public acknowledge(runId: string, reply: MacroPackageRunReply): void {
    const run = this.runs.get(runId);
    if (!run || reply.runId !== runId || !this.samePackage(run, reply)) throw new Error('local_package_native_identity_invalid');
    if (isTerminal(reply)) this.retain(run, reply);
  }
  private samePackage(run: OwnedRun, reply: MacroPackageRunReply): boolean {
    // Completed loaded packages must report the exact SHA; pre-load failures/cancellation may omit it.
    return reply.revision === run.packageSha256 || (!reply.revision && reply.outcome !== 'completed');
  }
  private retain(run: OwnedRun, reply: MacroPackageRunReply): void {
    const previous = run.terminal;
    if (previous && (previous.outcome !== 'failed' || !['completed', 'cancelled'].includes(reply.outcome))) return;
    run.terminal = reply;
    // Unconfirmed release must remain owned and eligible for cleanup, even after many later runs.
    if (reply.errorCode === 'input_release_unconfirmed' || (previous && previous.errorCode !== 'input_release_unconfirmed')) return;
    this.terminalOrder.push(reply.runId);
    while (this.terminalOrder.length > this.terminalLimit) this.runs.delete(this.terminalOrder.shift()!);
  }
}
